import { motion } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import SectionHeading from "@/components/SectionHeading";
import CTASection from "@/components/CTASection";
import { useLanguage } from "@/hooks/useLanguage";
import { Target, TrendingUp, Handshake, CheckCircle2, MapPin, Phone, Mail } from "lucide-react";

export default function About() {
  const { t } = useLanguage();

  const principles = [
    { icon: Target, titleKey: "precision", descKey: "precisionDesc", color: "bg-primary-deep" },
    { icon: TrendingUp, titleKey: "performance", descKey: "performanceDesc", color: "bg-secondary-blue" },
    { icon: Handshake, titleKey: "promise", descKey: "promiseDesc", color: "bg-gold" },
  ];

  const whatWeSupport = [
    "Industrial trading & supply",
    "Electrical material requirements",
    "Mechanical material requirements",
    "Civil material requirements",
    "CNC laser cut components",
    "Press brake formed parts",
    "Tube & pipe fabricated items",
    "Steel fabrication",
    "Powder coating",
    "Epoxy coating",
    "BOQ-based project support",
    "Drawing-based fabrication",
  ];

  return (
    <>
      <PageHeader titleKey="aboutPageTitle" subtitleKey="aboutPageSubtitle" />

      <section className="bg-white section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }} className="lg:col-span-5 relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-gold/10 rounded-2xl -z-10" />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-secondary-blue/10 rounded-2xl -z-10" />
              <img src="/images/about-facility.jpg" alt="Sky Mark Co." className="w-full rounded-2xl aspect-video object-cover shadow-lg" loading="lazy" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-md">
                <p className="font-display font-bold text-sm text-primary-deep">Sky Mark Co.</p>
                <p className="text-xs text-steel">{t("heroLocation")}</p>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="lg:col-span-7">
              <SectionHeading overlineKey="ourCompany" titleKey="precisionPerformancePromise" align="left" />
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="bg-steel-light rounded-xl p-5">
                  <h4 className="font-display font-bold text-primary-deep mb-2 text-sm uppercase tracking-wide">{t("whatWeDoCard")}</h4>
                  <p className="text-sm text-steel leading-relaxed">{t("whatWeDoCardDesc")}</p>
                </div>
                <div className="bg-steel-light rounded-xl p-5">
                  <h4 className="font-display font-bold text-primary-deep mb-2 text-sm uppercase tracking-wide">{t("howWeWorkCard")}</h4>
                  <p className="text-sm text-steel leading-relaxed">{t("howWeWorkCardDesc")}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-steel-light section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="text-center mb-10">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="label-overline text-steel mb-2">{t("ourValues")}</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="font-display font-bold text-2xl md:text-3xl text-primary-deep">{t("threePrinciples")}</motion.h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {principles.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div key={p.titleKey} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.12 }} className="bg-white rounded-2xl p-8 text-center transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1 border border-muted-border/50">
                  <div className={`w-16 h-16 ${p.color} rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-md`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-graphite mb-2">{t(p.titleKey)}</h3>
                  <p className="text-steel">{t(p.descKey)}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Chairman's Message */}
      <section className="bg-primary-deep section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <p className="label-overline text-gold-base mb-4 text-center">{t("chairmanTitle")}</p>
            <h2 className="font-display font-extrabold text-white text-2xl md:text-3xl lg:text-4xl text-center mb-10">
              {t("chairmanMessage")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Chairman Photo */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="lg:col-span-4 flex flex-col items-center"
            >
              <div className="relative">
                <div className="absolute -top-3 -left-3 w-full h-full border-2 border-gold/40 rounded-2xl" />
                <img
                  src="/images/chairman.jpg"
                  alt={t("chairmanName")}
                  className="relative w-64 h-80 md:w-72 md:h-96 object-cover rounded-2xl shadow-2xl"
                  loading="lazy"
                />
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-xl px-5 py-3 shadow-lg border border-muted-border whitespace-nowrap">
                  <p className="font-display font-bold text-sm text-primary-deep text-center">{t("chairmanName")}</p>
                  <p className="text-xs text-steel text-center">{t("chairmanTitle")}</p>
                </div>
              </div>
            </motion.div>

            {/* Message Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-8"
            >
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8">
                <p className="text-gold-base font-display font-bold text-lg mb-4">{t("chairmanGreeting")}</p>
                <div className="space-y-4 text-white/80 leading-relaxed text-base">
                  <p>{t("chairmanP1")}</p>
                  <p>{t("chairmanP2")}</p>
                  <p>{t("chairmanP3")}</p>
                </div>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-white/50 text-sm mb-1">{t("chairmanClosing")}</p>
                  <p className="font-display font-bold text-xl text-white">{t("chairmanName")}</p>
                  <p className="text-gold-base text-sm">{t("chairmanTitle")}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-white section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h3 className="font-display font-bold text-xl text-primary-deep mb-5">{t("whatWeSupport")}</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {whatWeSupport.map((item) => (
                  <div key={item} className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                    <span className="text-sm text-graphite">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}>
              <h3 className="font-display font-bold text-xl text-primary-deep mb-5">{t("ourMarketFocus")}</h3>
              <div className="space-y-4">
                {[
                  { icon: MapPin, title: t("infraConstruction"), desc: t("infraConstructionDesc") },
                  { icon: Phone, title: t("contractorLed"), desc: t("contractorLedDesc") },
                  { icon: Mail, title: t("materialPackages"), desc: t("materialPackagesDesc") },
                ].map((card, i) => {
                  const Icon = card.icon;
                  return (
                    <div key={i} className="flex items-start gap-4 p-4 bg-steel-light rounded-xl">
                      <div className="w-10 h-10 rounded-lg bg-primary-deep/10 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-primary-deep" />
                      </div>
                      <div>
                        <p className="font-medium text-graphite text-sm">{card.title}</p>
                        <p className="text-xs text-steel mt-1">{card.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection titleKey="workWithUs" descKey="workWithUsDesc" bgColor="dark" />
    </>
  );
}
